import { el, button } from "../lib/dom.ts";
import { trapFocus } from "../lib/focusTrap.ts";
import { syncOverlayBackground } from "../lib/overlayBackground.ts";

// Shared bottom-sheet drawer scaffold (scrim + sheet + grip + "Done" + focus trap),
// extracted from the byte-identical bookmarks/savedPaths copies that had begun to
// drift. `className` is the sheet's modifier class (e.g. "bookmarks-drawer"); the
// sheet always also carries the base "journey-drawer" class. `ariaLabel` names the
// dialog and its close button ("Close <ariaLabel>"). Each caller supplies a buildBody
// thunk; open() renders it, refresh() re-renders it (preserving focus), close()
// tears the sheet down and (by default) returns focus to whatever opened it.
//
// NOTE: this scaffold covers the two STANDALONE drawers only. The Source Desk drawer
// (verseHead pinning + scroll handler + onBeforeDrawerOpen) and the navigator
// journey/explore drawers (module-shared bodies that refreshJourney/refreshExplore
// write into across the closure) carry site-specific behaviour and are intentionally
// NOT routed through here - forcing them in would unify functional differences.
export interface DrawerController {
  // Open rendering buildBody() into the body; if already open, TOGGLE closed.
  open(buildBody: () => HTMLElement): void;
  // Re-render the body using the most recent buildBody, preserving focus.
  refresh(): void;
  // Close; restoreFocus (default true) returns focus to the opener element.
  close(restoreFocus?: boolean): void;
  isOpen(): boolean;
}

export function createDrawer(className: string, ariaLabel: string): DrawerController {
  let body: HTMLElement | null = null;
  let scrim: HTMLElement | null = null;
  let release: (() => void) | null = null;
  let opener: HTMLElement | null = null;
  let lastBuild: (() => HTMLElement) | null = null;

  // Focus restore inside the body: first button, falling back to the close button.
  function restoreBodyFocus(): void {
    if (!body) return;
    const next =
      body.querySelector<HTMLButtonElement>("button") ??
      body.closest(`.${className}`)?.querySelector<HTMLButtonElement>(".drawer-close") ??
      null;
    next?.focus();
  }

  function open(buildBody: () => HTMLElement): void {
    if (body) {
      close();
      return;
    }
    lastBuild = buildBody;
    opener = (document.activeElement as HTMLElement) ?? null;
    const sc = el("div", "drawer-scrim");
    const sheet = el("div", `journey-drawer ${className}`);
    sheet.setAttribute("role", "dialog");
    sheet.setAttribute("aria-modal", "true");
    sheet.setAttribute("aria-label", ariaLabel);
    const grip = el("div", "drawer-grip");
    grip.setAttribute("aria-hidden", "true");
    const closeBtn = button("drawer-close", "Done", () => close());
    closeBtn.setAttribute("aria-label", `Close ${ariaLabel}`);
    const bodyWrap = el("div", "drawer-body");
    bodyWrap.append(buildBody());
    sheet.append(grip, closeBtn, bodyWrap);
    sc.addEventListener("click", () => close());
    document.body.append(sc, sheet);
    syncOverlayBackground(); // inert the app behind the drawer
    body = bodyWrap;
    scrim = sc;
    const handle = trapFocus(sheet, { initialFocus: closeBtn, onEscape: () => close() });
    release = () => handle.release();
  }

  function refresh(): void {
    if (!body || !lastBuild) return;
    const hadFocus = document.activeElement instanceof HTMLElement && body.contains(document.activeElement);
    body.replaceChildren(lastBuild());
    if (hadFocus) restoreBodyFocus();
  }

  function close(restoreFocus = true): void {
    release?.();
    release = null;
    scrim?.remove();
    body?.closest(`.${className}`)?.remove();
    body = null;
    scrim = null;
    syncOverlayBackground(); // un-inert the background BEFORE returning focus to it
    const o = opener;
    opener = null;
    if (restoreFocus) o?.focus?.();
  }

  return { open, refresh, close, isOpen: () => !!body };
}
